/**
 * Cross-fleet leads pipeline — operator view of upgrade opportunities.
 * Sortable table of property_recommendations across the fleet.
 */

import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ArrowUpDown, Lightbulb, X, Filter,
  BatteryCharging, Sun, Clock, AlertTriangle, CalendarPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurtailmentDollars } from '@/components/dashboard/CurtailmentCard';
import { cn } from '@/lib/utils';
import { useCreateIssueFromRecommendation } from '@/hooks/useServiceIssues';
import { ScheduleVisitDialog } from '@/components/visits/ScheduleVisitDialog';
import {
  buildRecommendationIssueTitle,
  buildRecommendationIssueDescription,
  buildRecommendationTriggerSnapshot,
  mapRecommendationToIssueType,
  mapRecommendationToSeverity,
  mapRecommendationToVisitType,
  buildVisitInternalNotes,
} from '@/lib/recommendation-actions';

interface OpportunityRow {
  id: string;
  property_id: string;
  property_name: string;
  recommendation_type: string;
  priority: string;
  annual_waste_dollars: number | null;
  annual_avoidable_import_dollars: number | null;
  estimated_annual_savings: number | null;
  estimated_cost: number | null;
  estimated_payback_years: number | null;
  confidence: string;
  data_days: number;
  rate_source: string;
  evaluated_at: string;
}

const TYPE_LABELS: Record<string, string> = {
  battery_expansion: 'Battery Expansion',
  pv_addition: 'Solar Addition',
  tou_shift: 'TOU Optimization',
  load_management: 'Load Management',
  no_action: 'Well-Sized',
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  battery_expansion: <BatteryCharging className="w-3.5 h-3.5 text-wt-amber-primary" />,
  pv_addition: <Sun className="w-3.5 h-3.5 text-wt-amber-primary" />,
  tou_shift: <Clock className="w-3.5 h-3.5 text-blue-400" />,
};

const PRIORITY_COLORS: Record<string, string> = {
  high: 'text-red-400 border-red-400/30',
  medium: 'text-amber-400 border-amber-400/30',
  low: 'text-blue-400 border-blue-400/30',
  info: 'text-zinc-400 border-zinc-400/30',
};

type SortKey = 'waste' | 'savings' | 'payback' | 'priority';

export default function Opportunities() {
  const { organization, properties } = useApp();
  const queryClient = useQueryClient();
  const [sortBy, setSortBy] = useState<SortKey>('waste');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [creatingIssue, setCreatingIssue] = useState<string | null>(null);
  const [visitDialogRow, setVisitDialogRow] = useState<OpportunityRow | null>(null);
  const createIssue = useCreateIssueFromRecommendation();

  const propertyMap = useMemo(
    () => new Map(properties.map((p) => [p.id, p.name])),
    [properties],
  );

  const { data: opportunities, isLoading } = useQuery({
    queryKey: ['fleet-opportunities', organization?.id],
    queryFn: async (): Promise<OpportunityRow[]> => {
      const propertyIds = properties.map((p) => p.id);
      if (propertyIds.length === 0) return [];

      const { data, error } = await supabase
        .from('property_recommendations')
        .select('id, property_id, recommendation_type, priority, annual_waste_dollars, annual_avoidable_import_dollars, estimated_annual_savings, estimated_cost, estimated_payback_years, confidence, data_days, rate_source, evaluated_at')
        .in('property_id', propertyIds)
        .is('dismissed_at', null)
        .neq('recommendation_type', 'no_action')
        .order('annual_waste_dollars', { ascending: false, nullsFirst: false });

      if (error) throw error;

      return (data ?? []).map((r) => ({
        ...r,
        property_name: propertyMap.get(r.property_id) ?? 'Unknown',
      })) as OpportunityRow[];
    },
    enabled: !!organization?.id && properties.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const filtered = useMemo(() => {
    let rows = opportunities ?? [];
    if (filterType !== 'all') rows = rows.filter((r) => r.recommendation_type === filterType);
    if (filterPriority !== 'all') rows = rows.filter((r) => r.priority === filterPriority);

    rows.sort((a, b) => {
      switch (sortBy) {
        case 'waste': return (b.annual_waste_dollars ?? 0) - (a.annual_waste_dollars ?? 0);
        case 'savings': return (b.estimated_annual_savings ?? 0) - (a.estimated_annual_savings ?? 0);
        case 'payback': return (a.estimated_payback_years ?? 999) - (b.estimated_payback_years ?? 999);
        case 'priority': {
          const order: Record<string, number> = { high: 0, medium: 1, low: 2, info: 3 };
          return (order[a.priority] ?? 3) - (order[b.priority] ?? 3);
        }
        default: return 0;
      }
    });

    return rows;
  }, [opportunities, filterType, filterPriority, sortBy]);

  const handleCreateIssue = (row: OpportunityRow) => {
    if (!organization?.id) return;
    setCreatingIssue(row.id);
    createIssue.mutate(
      {
        propertyId: row.property_id,
        organizationId: organization.id,
        recommendationId: row.id,
        issueType: mapRecommendationToIssueType(row.recommendation_type),
        severity: mapRecommendationToSeverity(row.priority),
        title: buildRecommendationIssueTitle(row),
        description: buildRecommendationIssueDescription(row),
        triggerSnapshot: buildRecommendationTriggerSnapshot(row),
      },
      {
        onSuccess: () => {
          toast.success('Service issue created from recommendation');
          queryClient.invalidateQueries({ queryKey: ['fleet-opportunities'] });
        },
        onError: (err) => {
          toast.error(`Failed to create issue: ${err instanceof Error ? err.message : 'Unknown error'}`);
        },
        onSettled: () => setCreatingIssue(null),
      },
    );
  };

  const handleDismiss = async (id: string) => {
    setDismissing(id);
    try {
      const { error } = await supabase
        .from('property_recommendations')
        .update({ dismissed_at: new Date().toISOString(), dismissal_reason: 'operator_dismissed' })
        .eq('id', id);
      if (error) throw error;
      toast.success('Recommendation dismissed');
      queryClient.invalidateQueries({ queryKey: ['fleet-opportunities'] });
    } catch {
      toast.error('Failed to dismiss');
    } finally {
      setDismissing(null);
    }
  };

  const totalWaste = (opportunities ?? []).reduce((s, r) => s + (r.annual_waste_dollars ?? 0), 0);
  const totalSavings = (opportunities ?? []).reduce((s, r) => s + (r.estimated_annual_savings ?? 0), 0);

  return (
    <div className="p-4 sm:p-6 max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Upgrade Opportunities</h1>
        <p className="text-wt-text-secondary mt-1">
          Properties with actionable recommendations across your fleet
        </p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-red-400">{formatCurtailmentDollars(totalWaste)}</p>
            <p className="text-xs text-muted-foreground">Annual waste across fleet</p>
          </CardContent>
        </Card>
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-wt-green-primary">{formatCurtailmentDollars(totalSavings)}</p>
            <p className="text-xs text-muted-foreground">Potential annual savings</p>
          </CardContent>
        </Card>
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold">{filtered.length}</p>
            <p className="text-xs text-muted-foreground">Active recommendations</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters + Sort */}
      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-44"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="battery_expansion">Battery Expansion</SelectItem>
            <SelectItem value="pv_addition">Solar Addition</SelectItem>
            <SelectItem value="tou_shift">TOU Optimization</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All priorities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortKey)}>
          <SelectTrigger className="w-40">
            <ArrowUpDown className="w-3 h-3 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="waste">$ Wasted</SelectItem>
            <SelectItem value="savings">$ Savings</SelectItem>
            <SelectItem value="payback">Payback</SelectItem>
            <SelectItem value="priority">Priority</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-8">
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 rounded bg-muted/30 animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-12 text-center">
            <Lightbulb className="h-10 w-10 text-wt-text-secondary mx-auto mb-3" />
            <p className="text-wt-text-secondary">
              {(opportunities ?? []).length === 0
                ? 'No recommendations yet. The weekly evaluation runs on Mondays.'
                : 'No recommendations match your filters.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-wt-bg-secondary border-border overflow-hidden">
          <CardHeader className="pb-0 px-0">
            <div className="grid grid-cols-[1fr_140px_100px_100px_90px_80px_120px] gap-2 px-4 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium border-b border-border">
              <span>Property</span>
              <span>Type</span>
              <span className="text-right">$ Wasted/yr</span>
              <span className="text-right">$ Savings/yr</span>
              <span className="text-right">Payback</span>
              <span className="text-center">Confidence</span>
              <span className="text-right">Actions</span>
            </div>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-border/50">
            {filtered.map((row) => (
              <div
                key={row.id}
                className="grid grid-cols-[1fr_140px_100px_100px_90px_80px_120px] gap-2 px-4 py-2.5 items-center hover:bg-muted/20 transition-colors text-sm"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className={cn('text-[10px] flex-shrink-0', PRIORITY_COLORS[row.priority])}>
                    {row.priority}
                  </Badge>
                  <Link
                    to={`/app/property/${row.property_id}`}
                    className="text-foreground hover:underline truncate"
                  >
                    {row.property_name}
                  </Link>
                </div>
                <div className="flex items-center gap-1.5">
                  {TYPE_ICONS[row.recommendation_type]}
                  <span className="text-xs text-muted-foreground truncate">
                    {TYPE_LABELS[row.recommendation_type] ?? row.recommendation_type}
                  </span>
                </div>
                <span className="text-right font-mono text-xs">
                  {row.annual_waste_dollars != null ? formatCurtailmentDollars(row.annual_waste_dollars) : '—'}
                </span>
                <span className="text-right font-mono text-xs text-wt-green-primary">
                  {row.estimated_annual_savings != null ? formatCurtailmentDollars(row.estimated_annual_savings) : '—'}
                </span>
                <span className="text-right font-mono text-xs">
                  {row.estimated_payback_years != null ? `${row.estimated_payback_years} yr` : '—'}
                </span>
                <span className="text-center">
                  <Badge variant="outline" className="text-[10px] border-wt-border-subtle text-muted-foreground">
                    {row.confidence}
                  </Badge>
                </span>
                <div className="flex justify-end gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-amber-400"
                        onClick={() => handleCreateIssue(row)}
                        disabled={creatingIssue === row.id}
                      >
                        <AlertTriangle className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">Create Issue</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-blue-400"
                        onClick={() => setVisitDialogRow(row)}
                      >
                        <CalendarPlus className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">Schedule Visit</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400"
                        onClick={() => handleDismiss(row.id)}
                        disabled={dismissing === row.id}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">Dismiss</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Schedule Visit dialog — pre-filled from recommendation */}
      {visitDialogRow && (
        <ScheduleVisitDialog
          open={!!visitDialogRow}
          onOpenChange={(open) => { if (!open) setVisitDialogRow(null); }}
          preselectedPropertyId={visitDialogRow.property_id}
          prefilledVisitType={mapRecommendationToVisitType(visitDialogRow.recommendation_type)}
          prefilledInternalNotes={buildVisitInternalNotes(visitDialogRow)}
        />
      )}
    </div>
  );
}
